/**
 * Fake Line Item Properties Manager
 *
 * Manages product properties via browser localStorage instead of native Shopify line item properties.
 * Properties are displayed in cart/mini-cart but NOT sent to checkout.
 *
 * Storage structure:
 * {
 *   "variant_id:key": "value"
 * }
 */

class FakePropertiesManager {
  constructor() {
    this.storageKey = "ezup_fake_properties";
    this.refreshTimeout = null;
    this.isRefreshing = false; // Flag to prevent re-entrant refreshes
    this.lastRefreshTime = 0; // Track last refresh timestamp
    this.minRefreshInterval = 500; // Minimum time between refreshes (ms)
    this.init();
  }

  init() {
    // Listen for product:added event (fired by theme after successful add to cart)
    document.addEventListener("product:added", (event) => {
      console.log("Product added event detected", event);
    });

    // Listen for variant changes to update data-variant-id on inputs
    document.addEventListener("variant:changed", (event) =>
      this.handleVariantChange(event)
    );

    // Intercept add to cart button clicks to capture properties BEFORE submission
    document.addEventListener(
      "click",
      (event) => {
        const addToCartButton = event.target.closest(
          '[data-action="add-to-cart"]'
        );
        if (addToCartButton) {
          this.capturePropertiesFromButton(addToCartButton);
        }
      },
      true
    ); // Use capture phase to run before theme handler

    // Re-populate fake properties after AJAX cart rerender (cart page path)
    document.addEventListener("cart:rerendered", () => {
      this.repopulateCartPageProperties();
      this.repopulateMiniCartProperties();
    });

    // Initial cleanup on page load
    this.cleanupOrphanedProperties();
    if (document.readyState === "loading") {
      document.addEventListener(
        "DOMContentLoaded",
        () => this.repopulateMiniCartProperties(),
        { once: true }
      );
    } else {
      this.repopulateMiniCartProperties();
    }
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  getFallbackSku(container) {
    return String(container.getAttribute("data-fallback-sku") || "").trim();
  }

  propertyIsSku(prop) {
    return String((prop && prop.first) || "").toUpperCase().includes("SKU");
  }

  normalizePropertyLabel(label) {
    return String(label || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/gi, " ")
      .trim()
      .toLowerCase();
  }

  isFrenchLocale() {
    return String(document.documentElement.lang || "")
      .toLowerCase()
      .startsWith("fr");
  }

  getLocalizedPropertyLabel(label) {
    if (!this.isFrenchLocale() || this.propertyIsSku({ first: label })) {
      return label;
    }

    const labels = {
      "top color": "Couleur du toit",
      "frame color": "Couleur de la structure",
      color: "Couleur",
      size: "Taille",
      "canopy size": "Taille de l’abri",
      "fabric type": "Type de tissu",
      "banner size": "Taille de la bannière",
      "leg type": "Type de pied",
      "leg shape": "Type de pied",
      "flag size": "Taille du drapeau",
      "print surface": "Surface d’impression",
      "flag base": "Base du drapeau",
    };

    return labels[this.normalizePropertyLabel(label)] || label;
  }

  getLocalizedPropertyValue(value) {
    if (!this.isFrenchLocale()) return value;

    const values = {
      black: "Noir",
      white: "Blanc",
      red: "Rouge",
      blue: "Bleu",
      "royal blue": "Bleu royal",
      "navy blue": "Bleu marine",
      "steel gray": "Gris acier",
      "steel grey": "Gris acier",
      "charcoal gray": "Gris charbon",
      "charcoal grey": "Gris charbon",
      gray: "Gris",
      grey: "Gris",
      orange: "Orange",
      "steel orange": "Orange acier",
      yellow: "Jaune",
      green: "Vert",
      "kelly green": "Vert Kelly",
      "forest green": "Vert forêt",
      limeade: "Limonade",
      yes: "Oui",
      no: "Non",
      available: "Disponible",
      "not available": "Non disponible",
    };

    return values[String(value || "").trim().toLowerCase()] || value;
  }

  getPropertyDisplayRank(prop) {
    if (this.propertyIsSku(prop)) return 0;

    const label = this.normalizePropertyLabel(prop && prop.first);
    const ranks = {
      // Counter / bar top PDP order
      "fabric type": 10,
      "type de tissu": 10,
      "banner size": 20,
      "taille de la banniere": 20,
      "leg type": 30,
      "leg shape": 30,
      "type de pied": 30,

      // Flag PDP order
      "flag size": 10,
      "taille du drapeau": 10,
      "print surface": 20,
      "surface d impression": 20,

      // Shared trailing options
      color: 40,
      couleur: 40,
      size: 50,
      taille: 50,
      "flag base": 50,
      "base du drapeau": 50,
    };

    return Object.prototype.hasOwnProperty.call(ranks, label)
      ? ranks[label]
      : 100;
  }

  getVisiblePropertiesForRender(properties, fallbackSku) {
    const propertyList = Array.isArray(properties) ? properties : [];
    const visibleProperties = propertyList.filter((prop) => {
      const first = String(prop.first || "");
      const last = String(prop.last || "");
      const firstChar = first.charAt(0);
      return (
        firstChar !== "_" &&
        first !== "Email" &&
        first !== "Logo" &&
        last.trim() !== ""
      );
    });

    if (
      fallbackSku &&
      !visibleProperties.some((prop) => this.propertyIsSku(prop))
    ) {
      visibleProperties.unshift({
        first: "SKU",
        last: fallbackSku,
      });
    }

    visibleProperties.sort(
      (left, right) =>
        this.getPropertyDisplayRank(left) - this.getPropertyDisplayRank(right)
    );

    return visibleProperties;
  }

  getDisplayPropertyValue(value) {
    const stringValue = String(value || "");
    const parts = stringValue.split("-");
    let longestPart = stringValue;
    let longestLength = 0;

    parts.forEach((part) => {
      if (part.length > longestLength) {
        longestPart = part;
        longestLength = part.length;
      }
    });

    return longestPart;
  }

  /**
   * Render properties for mini-cart
   */
  renderMiniCartProperties(container, variantId) {
    if (!variantId) return;

    const properties = this.getPropertiesForDisplay(variantId);
    const fallbackSku = this.getFallbackSku(container);
    console.log(
      "Mini-cart: Rendering properties for variant",
      variantId,
      properties
    );

    const visibleProperties = this.getVisiblePropertiesForRender(
      properties,
      fallbackSku
    );

    if (visibleProperties.length === 0) {
      container.innerHTML = "";
      return;
    }

    let html = '<ul class="mini-cart__property-list" role="list">';
    let detailsOpen = false;

    visibleProperties.forEach((prop) => {
      const label = this.escapeHtml(this.getLocalizedPropertyLabel(prop.first));
      const value = this.escapeHtml(
        this.propertyIsSku(prop)
          ? prop.last
          : this.getLocalizedPropertyValue(this.getDisplayPropertyValue(prop.last))
      );

      if (this.propertyIsSku(prop)) {
        html += `
          <li class="mini-cart__property mini-cart__property--sku">
            <span class="mini-cart__property-label">${label}:</span>
            <span class="mini-cart__property-value">${value}</span>
          </li>
        `;
      } else {
        if (!detailsOpen) {
          html += `
            <details class="mini-cart__properties-details">
              <summary class="mini-cart__properties-summary">
                <span class="mini-cart__properties-summary-text">See details</span>
                <span class="mini-cart__properties-summary-icon"><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon--arrow-bottom" viewBox="0 0 12 8"><path d="M1.2 1.2l4.8 4.8 4.8-4.8" stroke="currentColor" stroke-width="2" fill="none"/></svg></span>
              </summary>
              <div class="mini-cart__properties-group">
          `;
          detailsOpen = true;
        }

        html += `
          <li class="mini-cart__property">
            <span class="mini-cart__property-label">${label}:</span>
            <span class="mini-cart__property-value">${value}</span>
          </li>
        `;
      }
    });

    if (detailsOpen) {
      html += "</div></details>";
    }

    html += "</ul>";
    container.innerHTML = html;
    console.log("Mini-cart properties rendered successfully");
  }

  repopulateMiniCartProperties() {
    const containers = document.querySelectorAll(".mini-cart__fake-properties");
    containers.forEach((container) => {
      const variantId = container.getAttribute("data-variant-id");
      this.renderMiniCartProperties(container, variantId);
    });
  }

  /**
   * Re-populate fake properties on the cart page after AJAX rerender.
   * Inline <script> tags don't execute when injected via innerHTML,
   * so we need to manually render properties into the containers.
   */
  repopulateCartPageProperties() {
    const cartSection = document.querySelector('[data-section-type="cart"]');
    if (!cartSection) return;

    const containers = cartSection.querySelectorAll(".line-item__fake-properties");
    containers.forEach((container) => {
      const variantId = container.getAttribute("data-variant-id");
      this.renderCartPageProperties(container, variantId);
    });
  }

  /**
   * Render properties for the cart page (uses line-item__* classes)
   */
  renderCartPageProperties(container, variantId) {
    if (!variantId) return;

    const properties = this.getPropertiesForDisplay(variantId);
    const fallbackSku = this.getFallbackSku(container);
    const visibleProperties = this.getVisiblePropertiesForRender(
      properties,
      fallbackSku
    );

    if (visibleProperties.length === 0) {
      container.innerHTML = "";
      return;
    }

    let html = '<ul class="line-item__property-list" role="list">';

    visibleProperties.forEach((prop) => {
      const skuClass = this.propertyIsSku(prop) ? " line-item__property--sku" : "";
      const label = this.escapeHtml(this.getLocalizedPropertyLabel(prop.first));
      const value = this.escapeHtml(
        this.propertyIsSku(prop)
          ? prop.last
          : this.getLocalizedPropertyValue(this.getDisplayPropertyValue(prop.last))
      );

      html += `
        <li class="line-item__property${skuClass}">
          <span class="line-item__property-label">${label}:</span>
          <span class="line-item__property-value">${value}</span>
        </li>
      `;
    });

    html += "</ul>";
    container.innerHTML = html;
  }

  /**
   * Capture properties from fake property inputs when clicking add to cart button
   */
  capturePropertiesFromButton(button) {
    const form = button.closest('form[action*="/cart/add"]');
    if (!form) {
      console.warn("Add to cart button not inside a cart form");
      return;
    }

    console.log("Capturing properties from form:", form);

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    console.log("Found fake property inputs:", fakeInputs.length);

    fakeInputs.forEach((input) => {
      const propertyName = input.getAttribute("data-fake-property");
      const variantId = input.getAttribute("data-variant-id");
      const value = input.value;

      console.log("Processing input:", { propertyName, variantId, value });

      if (propertyName && variantId && value) {
        this.setProperty(variantId, propertyName, value);
        console.log(
          `Saved fake property: ${variantId}:${propertyName} = ${value}`
        );
      }
    });

    // Log current storage state
    console.log("Current fake properties storage:", this.getAllProperties());
  }

  /**
   * Capture properties from fake property inputs (legacy method for events)
   */
  captureProperties(event) {
    const form = event.target.closest("form");
    if (!form) return;

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    fakeInputs.forEach((input) => {
      const propertyName = input.getAttribute("data-fake-property");
      const variantId = input.getAttribute("data-variant-id");
      const value = input.value;

      if (propertyName && variantId && value) {
        this.setProperty(variantId, propertyName, value);
      }
    });
  }

  /**
   * Update data-variant-id on fake property inputs when variant changes
   */
  handleVariantChange(event) {
    const { variant } = event.detail;
    if (!variant || !variant.id) return;

    const form =
      event.target.closest("form") ||
      document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    const fakeInputs = form.querySelectorAll(".fake-property-input");
    fakeInputs.forEach((input) => {
      input.setAttribute("data-variant-id", variant.id);

      // Update option values
      if (input.classList.contains("product-property__option-1")) {
        input.value = variant.option1 || "";
      } else if (input.classList.contains("product-property__option-2")) {
        input.value = variant.option2 || "";
      } else if (input.classList.contains("product-property__option-3")) {
        input.value = variant.option3 || "";
      } else if (input.classList.contains("product-property__sku")) {
        input.value = variant.sku || "";
      }
    });
  }

  /**
   * Get storage key for a specific variant and property
   */
  getStorageKey(variantId, propertyName) {
    return `${variantId}:${propertyName}`;
  }

  /**
   * Get all properties from localStorage
   */
  getAllProperties() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : {};
    } catch (e) {
      console.error("Error reading fake properties:", e);
      return {};
    }
  }

  /**
   * Save all properties to localStorage
   */
  saveAllProperties(properties) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(properties));
    } catch (e) {
      console.error("Error saving fake properties:", e);
    }
  }

  /**
   * Set a property value for a specific variant
   */
  setProperty(variantId, propertyName, value) {
    const properties = this.getAllProperties();
    const key = this.getStorageKey(variantId, propertyName);

    if (value && value.trim() !== "") {
      properties[key] = value;
    } else {
      delete properties[key];
    }

    this.saveAllProperties(properties);
  }

  /**
   * Get a property value for a specific variant
   */
  getProperty(variantId, propertyName) {
    const properties = this.getAllProperties();
    const key = this.getStorageKey(variantId, propertyName);
    return properties[key] || null;
  }

  /**
   * Get all properties for a specific variant
   */
  getPropertiesForVariant(variantId) {
    const allProperties = this.getAllProperties();
    const variantProperties = {};
    const prefix = `${variantId}:`;

    Object.keys(allProperties).forEach((key) => {
      if (key.startsWith(prefix)) {
        const propertyName = key.substring(prefix.length);
        variantProperties[propertyName] = allProperties[key];
      }
    });

    return variantProperties;
  }

  /**
   * Remove all properties for a specific variant
   */
  removePropertiesForVariant(variantId) {
    const allProperties = this.getAllProperties();
    const prefix = `${variantId}:`;

    Object.keys(allProperties).forEach((key) => {
      if (key.startsWith(prefix)) {
        delete allProperties[key];
      }
    });

    this.saveAllProperties(allProperties);
  }

  /**
   * Clean up properties for variants no longer in cart
   */
  async cleanupOrphanedProperties() {
    try {
      // Fetch current cart
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Get all variant IDs currently in cart
      const cartVariantIds = new Set(
        cart.items.map((item) => item.variant_id.toString())
      );

      // Remove properties for variants not in cart
      const allProperties = this.getAllProperties();
      let hasChanges = false;

      Object.keys(allProperties).forEach((key) => {
        const variantId = key.split(":")[0];
        if (!cartVariantIds.has(variantId)) {
          delete allProperties[key];
          hasChanges = true;
        }
      });

      if (hasChanges) {
        this.saveAllProperties(allProperties);
      }
    } catch (e) {
      console.error("Error cleaning up fake properties:", e);
    }
  }

  /**
   * Clear all stored properties
   */
  clearAll() {
    localStorage.removeItem(this.storageKey);
  }

  /**
   * Get properties formatted for display (like Shopify's line_item.properties)
   */
  getPropertiesForDisplay(variantId) {
    const properties = this.getPropertiesForVariant(variantId);

    // Convert to array format similar to Shopify's properties
    return Object.entries(properties).map(([key, value]) => ({
      first: key,
      last: value,
    }));
  }
}

// Initialize global instance
if (
  !window.fakePropertiesManager ||
  window.fakePropertiesManager === undefined
) {
  window.fakePropertiesManager = new FakePropertiesManager();
}
